import { Button, dialog, InfiniteScroll, Progress } from "nq-component";
import BaseListPage from "../../../base/BaseListPage";
import {
  countObjectUseCase,
  deleteObjectUseCase,
  findObjectUseCase,
  getObjectUseCase,
  upsertUseCase,
} from "../../../usecases/object";
import ReportPagePresenter from "./ReportPagePresenter";
import CardView from "../../../components/common/CardView";
import SearchSelect from "../../../components/searchSelect/SearchSelect";
import DateRangePicker from "../../../components/common/DateRangePicker";
import InputFactory from "../../../components/InputFactory";
import Table from "../../../components/Table";
import withRouter from "../../../withRouter";
import LeavesRecordsForm from "./leaves-form/LeavesRecordsForm";
import AgeFilter from "../../../components/common/AgeFilter";
import Hierarchy from "../../../components/common/Hierarchy";
import ServiceRecordsForm from "./service-form/ServiceRecordsForm";
import SalaryAdjustmentPage from "./salary-adjustment/SalaryAdjustmentPage";
import StepIncrementPage from "./step-increment/StepIncrementPage";
import { Spin } from "antd";
import { ViewOrganizationalStructure } from "./office-assignment/ViewOrganizationStructure";
import ViewAssignedDevices from "./office-assignment/ViewAssignedDevices";
import PayrollReports from "./payroll-reports/PayrollReports";
import {
  DEFAULT_REPORT,
  buildSchemaFromColumns,
  MONTHS,
} from "./reportConfig";

const EMPLOYEE_POINTER_KEYS = [
  "biometricId",
  "agency",
  "Firstname",
  "Middlename",
  "surname",
  "region",
  "office",
  "division",
  "section",
  "unit",
  "cluster",
  "twentyFourSeven",
  "employmentType.name",
];

class ReportPage extends BaseListPage {
  constructor(props) {
    super(props);
    this.presenter = new ReportPagePresenter(
      this,
      findObjectUseCase(),
      countObjectUseCase(),
      deleteObjectUseCase(),
      upsertUseCase(),
    );
    this.state = {
      objects: [],
      count: 0,
      limit: 50,
      current: 1,
      designationList: [],
      committeeList: [],
      isLoading: false,
      committee: {},
      construction: DEFAULT_REPORT,
      reportFilters: {},
      generated: false,
      summary: "",
      error: null,
      loading: false,
      filterEpoch: 0,
    };
  }

  getCollectionName() {
    const report = this.state.construction;
    if (report?.mode === "service") return report.key || "report_rows";
    return report?.collection || "employees";
  }

  async componentDidMount() {
    this.presenter.componentDidMount();
    // Committee data loads lazily when Committee Structure is selected.
    // Employee pointer loads when that filter is rendered for the selected report.
  }

  componentWillUnmount() {
    try {
      this.presenter?.reportService?.cancelRun?.();
      this.presenter?.findObjectUseCase?.abort?.();
    } catch (_) {
      /* ignore */
    }
  }

  async ensureCommitteeFiltersLoaded() {
    if (this.state.committeeList?.length || this._loadingCommittee) return;
    this._loadingCommittee = true;
    try {
      const [committeeList, designationList] = await Promise.all([
        findObjectUseCase().execute("committee", {
          where: { status: "active" },
          include: [
            "committeeRelation",
            "committeeRelation.employee",
            "committeeRelation.Designation",
          ],
        }),
        findObjectUseCase().execute("committeee_category"),
      ]);
      this.setState({ committeeList, designationList });
    } catch (e) {
      this.showError(e?.message || e);
    } finally {
      this._loadingCommittee = false;
    }
  }

  setCount(count) {
    this.setState({ count });
  }

  async refetchData() {
    return this.state.objects;
  }

  onClickPagination(page) {
    this.presenter.setCurrentPage(page);
  }

  setObjects(objects) {
    this.setState({ objects });
  }

  setCurrent(current) {
    this.setState({ current });
  }

  async viewOrgStructure(object) {
    const employee = await getObjectUseCase().execute("employees", object.id, {
      params: {
        select: ["region", "office", "division", "section", "unit", "cluster"],
      },
    });
    this.setState({ employee, viewOrg: true });
  }

  async viewAssignedBio(object) {
    const users = await findObjectUseCase().execute("users", {
      where: { "employee.id": object.id },
      keys: ["serialNum"],
    });
    if (users.length === 0) {
      this.showError("Invalid User");
      return;
    }
    this.setState({ assignedDevice: users[0].serialNum, viewBio: true });
  }

  getTableSchema(report) {
    if (report.mode === "component") return null;
    if (report.mode === "service") {
      return buildSchemaFromColumns(report.columns || [], report.key);
    }
    return this.getSchema(report.collection);
  }

  showBusyDialog(message) {
    dialog.fire({
      html: (
        <div className="d-flex gap-3 flex-column align-items-center justify-content-center p-5">
          <Spin size="large" />
          <p>{message || "Please wait..."}</p>
        </div>
      ),
      footer: false,
    });
  }

  renderFilterCol(key, children, colClass = "col-12 col-md-6 col-lg-4") {
    return (
      <div key={key} className={`${colClass} mb-2`}>
        {children}
      </div>
    );
  }

  renderStandardFilters(report) {
    const filters = report.filters || [];
    const rf = this.state.reportFilters || {};
    const { generated, isLoading } = this.state;
    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 5 }, (_, i) => currentYear - i);
    const hierarchyKey = `hierarchy-${report.key || report.name}-${this.state.filterEpoch}`;

    return (
      <div className="row g-2 align-items-end">
        {filters.map((def) => {
          if (def.type === "dateRange") {
            const filterKey = def.key || "dateRange";
            const fields = def.fields || ["start", "end"];
            return this.renderFilterCol(
              def.key,
              <div className="d-flex flex-column w-100">
                {def.label ? (
                  <label
                    className="mb-1"
                    style={{ fontSize: "0.9rem", fontWeight: 500 }}
                  >
                    {def.label}
                  </label>
                ) : null}
                <DateRangePicker
                  key={`${filterKey}-${report.key}-${this.state.filterEpoch}`}
                  width="100%"
                  fields={fields}
                  isArray={Boolean(def.isArray)}
                  onFilter={(filterObj) => {
                    if (Object.keys(filterObj).length > 0) {
                      // Normalize common start/end for summary + shared helpers
                      const start =
                        filterObj.start?.$gte ||
                        filterObj.createdAt?.$gte ||
                        filterObj.obDates?.$gte ||
                        filterObj.start ||
                        filterObj.$gte ||
                        null;
                      const end =
                        filterObj.end?.$lte ||
                        filterObj.dateApproved?.$lte ||
                        filterObj.createdAt?.$lte ||
                        filterObj.obDates?.$lte ||
                        filterObj.end ||
                        filterObj.$lte ||
                        null;
                      this.presenter.setReportFilter(filterKey, {
                        ...filterObj,
                        start,
                        end,
                      });
                    } else {
                      this.presenter.setReportFilter(filterKey, null);
                    }
                  }}
                />
              </div>,
            );
          }
          if (def.type === "month") {
            return this.renderFilterCol(
              def.key,
              <SearchSelect
                focus={true}
                placeholder={def.label || "Month"}
                value={rf.month || ""}
                onClickClear={() => this.presenter.setReportFilter("month", "")}
                onChange={(value) =>
                  this.presenter.setReportFilter("month", value)
                }
                options={MONTHS.map((m) => ({ label: m, value: m }))}
              />,
            );
          }
          if (def.type === "year") {
            return this.renderFilterCol(
              def.key,
              <SearchSelect
                focus={true}
                placeholder={def.label || "Year"}
                value={rf.year || ""}
                onClickClear={() => this.presenter.setReportFilter("year", "")}
                onChange={(value) =>
                  this.presenter.setReportFilter("year", value)
                }
                options={years.map((y) => ({
                  label: String(y),
                  value: String(y),
                }))}
              />,
            );
          }
          if (def.type === "employee") {
            const employeeFilterDisabled =
              report.key === "separated_employees" &&
              (!generated || isLoading);
            return this.renderFilterCol(
              def.key,
              <InputFactory
                key={`employee-${report.key}-${this.state.filterEpoch}`}
                type="Pointer"
                target="employees"
                field="employee"
                disabled={employeeFilterDisabled}
                object={
                  rf.employee ? { employee: rf.employee } : { employee: null }
                }
                include={["employmentType"]}
                keys={EMPLOYEE_POINTER_KEYS}
                onChange={(value) => {
                  if (employeeFilterDisabled) return;
                  this.presenter.setReportFilter("employee", value);
                }}
              />,
            );
          }
          if (def.type === "hierarchy") {
            return (
              <div key={def.key} className="col-12">
                <div className="row g-2">
                  <Hierarchy
                    key={hierarchyKey}
                    className="col-12 col-md-6 col-lg-4"
                    required={false}
                    handleObjectChange={(hierarchy) =>
                      this.presenter.handleHierarchyChange(hierarchy)
                    }
                  />
                </div>
              </div>
            );
          }
          if (def.type === "select") {
            return this.renderFilterCol(
              def.key,
              <SearchSelect
                focus={true}
                placeholder={def.label}
                value={
                  (def.options || []).find((o) => o.value === rf[def.key])
                    ?.label || ""
                }
                onClickClear={() =>
                  this.presenter.setReportFilter(def.key, "")
                }
                onChange={(value) =>
                  this.presenter.setReportFilter(def.key, value)
                }
                options={(def.options || []).map((o) => ({
                  label: o.label,
                  value: o.value,
                }))}
              />,
            );
          }
          return null;
        })}
      </div>
    );
  }

  renderLegacyFilters(report, schema) {
    const { committeeList, designationList } = this.state;
    const hierarchyKey = `legacy-hierarchy-${report.key || report.name}-${this.state.filterEpoch}`;
    return (
      <div className="row g-2 align-items-end">
        {report.isAgeBracket &&
          this.renderFilterCol(
            "age-filter",
            <AgeFilter
              field="age"
              onFilter={(filterObj) => {
                this.presenter.setReportFilter("collectionWhere", filterObj);
              }}
              key="age-filter"
            />,
            "col-12 col-md-6",
          )}
        {report.isCommitteeFilter && (
          <>
            {this.renderFilterCol(
              "committee",
              <SearchSelect
                focus={true}
                placeholder="Committee"
                value={this.state.committee?.committeeName}
                onChange={(value) =>
                  this.presenter.manualFilter(value, "committee")
                }
                onClickClear={() => {
                  this.presenter.manualFilter({}, "committee");
                  this.setState({ designation: {} });
                }}
                options={committeeList?.map((committee) => ({
                  label: committee.committeeName,
                  value: committee,
                }))}
              />,
            )}
            {this.renderFilterCol(
              "designation",
              <SearchSelect
                focus={true}
                placeholder="Designation"
                value={this.state.designation?.name}
                onChange={(value) =>
                  this.presenter.manualFilter(value, "designation")
                }
                disabled={Object.keys(this.state.committee || {}).length === 0}
                onClickClear={() => {
                  this.presenter.manualFilter({}, "designation");
                }}
                options={(() => {
                  const fromCommittee = (
                    this.state.committee?.committeeRelation || []
                  )
                    .map((rel) => rel?.Designation)
                    .filter((d) => d?.id)
                    .filter(
                      (d, i, arr) =>
                        arr.findIndex((x) => x.id === d.id) === i,
                    );
                  const source =
                    fromCommittee.length > 0
                      ? fromCommittee
                      : designationList || [];
                  return source.map((designation) => ({
                    label: designation.name,
                    value: designation,
                  }));
                })()}
              />,
            )}
          </>
        )}
        {report.isOrganizationFilter && (
          <div className="col-12">
            <div className="row g-2">
              <Hierarchy
                key={hierarchyKey}
                className="col-12 col-md-6 col-lg-4"
                required={false}
                handleObjectChange={(hierarchy) =>
                  this.presenter.handleHierarchyChange(hierarchy)
                }
              />
            </div>
          </div>
        )}
        {report.isHiredFilter &&
          this.renderFilterCol(
            "hired-range",
            <DateRangePicker
              fields={["createdAt", "updatedAt"]}
              onFilter={(filterObj) => {
                if (Object.keys(filterObj).length > 0) {
                  const updatedAtDate = new Date(filterObj.updatedAt.$lte);
                  const updatedFilterObj = {
                    hired: {
                      $gte: new Date(filterObj.createdAt.$gte)
                        .toISOString()
                        .split("T")[0],
                      $lte: updatedAtDate.toISOString().split("T")[0],
                    },
                  };
                  this.presenter.setReportFilter(
                    "collectionWhere",
                    updatedFilterObj,
                  );
                } else {
                  this.presenter.setReportFilter("collectionWhere", {});
                }
              }}
            />,
          )}
        {Array.isArray(report.filters) &&
          report.filters.length > 0 &&
          typeof report.filters[0] === "string" && (
            <>
              {report.isDateFilter &&
                this.renderFilterCol(
                  "legacy-date",
                  <DateRangePicker
                    fields={["createdAt", "updatedAt"]}
                    onFilter={(filterObj) => {
                      if (Object.keys(filterObj).length > 0) {
                        const updatedAtDate = new Date(
                          filterObj.updatedAt.$lte,
                        );
                        // Overtime Requests (only isDateFilter report): schema uses startTime/endTime
                        const startDay = new Date(filterObj.createdAt.$gte)
                          .toISOString()
                          .split("T")[0];
                        const endDay = updatedAtDate.toISOString().split("T")[0];
                        const updatedFilterObj = {
                          startTime: { $gte: startDay },
                          endTime: { $lte: `${endDay}T23:59:59.999Z` },
                        };
                        this.presenter.setReportFilter(
                          "collectionWhere",
                          updatedFilterObj,
                        );
                      } else {
                        this.presenter.setReportFilter("collectionWhere", {});
                      }
                    }}
                  />,
                )}
              {Object.keys(schema?.filters || {})
                .filter((field) => report.filters.includes(field))
                .map((field) => {
                  const { type, ...options } = schema.filters[field];
                  return this.renderFilterCol(
                    field,
                    <InputFactory
                      type={type}
                      field={field}
                      where={{}}
                      onChange={this.presenter.onChangeFilter.bind(this, type)}
                      {...options}
                    />,
                  );
                })}
            </>
          )}
      </div>
    );
  }

  renderActionBar() {
    const { isLoading, generated, count, objects } = this.state;
    const hasData = generated && (count > 0 || objects.length > 0);
    return (
      <div className="d-flex flex-wrap justify-content-end gap-2 mt-3 pt-3 border-top">
        <Button
          className="btn btn-primary"
          onClick={() => this.presenter.generateReport()}
          disabled={isLoading}
        >
          Generate
        </Button>
        <Button
          className="btn bg-white border border-secondary text-secondary"
          onClick={() => this.presenter.resetFilters()}
          disabled={isLoading}
        >
          Reset
        </Button>
        <Button
          className="btn bg-white border border-primary text-primary"
          onClick={() => this.presenter.exportToCSV()}
          disabled={isLoading || !hasData}
        >
          Export XLSX
        </Button>
      </div>
    );
  }

  render() {
    const {
      objects,
      object,
      loading,
      count,
      limit,
      current,
      construction,
      isLoading,
      generated,
      summary,
    } = this.state;
    const report = construction || DEFAULT_REPORT;
    const schema = this.getTableSchema(report);

    if (report.mode === "collection" && !schema) return <Progress />;

    if (
      report.mode === "collection" &&
      report.displayField?.includes("positionLabel") &&
      schema?.fields
    ) {
      schema.fields = {
        ...schema.fields,
        positionLabel: { type: "String", label: "Position" },
      };
    }

    const displayTable = report.displayField || Object.keys(schema?.fields || {});
    const includeTable =
      report.mode === "collection" && schema?.fields
        ? Object.keys(schema.fields).filter((key) => !displayTable.includes(key))
        : [];

    if (report.name === "Office Assignment") {
      includeTable.push(
        "region",
        "office",
        "division",
        "section",
        "unit",
        "cluster",
      );
    }

    const excludeFields =
      report.mode !== "collection"
        ? []
        : Object.keys(schema?.fields || {}).reduce(
            (acc, key) => {
              const options = schema.fields[key];
              if (options.read === false) acc.push(key);
              switch (options._type || options.type) {
                case "Relation":
                case "Array":
                case "Object":
                case "File":
                  acc.push(key);
                  break;
                default:
              }
              return acc;
            },
            [...includeTable, "code", "position", "nonRegPosition"],
          );

    return (
      <>
        <div className="overflow-auto" style={{ marginTop: "5rem" }}>
          <InfiniteScroll
            className="h-100"
            loadMore={
              report.mode === "component"
                ? () => {}
                : this.loadMore.bind(this)
            }
          >
            <div className="p-3 p-lg-4">
              <div
                className="d-flex mb-2"
                style={{ borderBottom: "1.5px solid #d3d3d3" }}
              >
                <p style={{ fontSize: ".9rem", color: "rgba(0, 0, 0, .45)" }}>
                  You are here: Home {">"} Human Resource {">"} Reports
                </p>
              </div>

              <div className="mb-3">
                <h3 className="text-primary mb-1">Reports</h3>
                <p className="text-muted mb-0" style={{ fontSize: ".9rem" }}>
                  Generate standard and user-defined HR reports. Select a
                  report, set filters, then click Generate.
                </p>
              </div>

              <CardView style={{ borderRadius: 0 }}>
                <label
                  className="form-label text-muted mb-1"
                  style={{ fontSize: ".8rem" }}
                >
                  Select report
                </label>
                <SearchSelect
                  focus={true}
                  value={report.name}
                  onChange={(value) =>
                    this.presenter.handelTableDataChange(value)
                  }
                  onClickClear={() => this.presenter.onClear()}
                  options={this.presenter.options.map((option) => ({
                    label: option.name,
                    value: option,
                  }))}
                />
                {report.description && (
                  <p
                    className="text-muted mt-2 mb-0"
                    style={{ fontSize: ".85rem" }}
                  >
                    {report.description}
                  </p>
                )}
              </CardView>

              {report.mode === "component" ? (
                <PayrollReports />
              ) : (
                <>
                  <div style={{ marginTop: "16px", width: "100%" }}>
                    <CardView style={{ borderRadius: 0 }}>
                      <p
                        className="text-muted mb-2"
                        style={{ fontSize: ".8rem", fontWeight: 600 }}
                      >
                        Filters
                      </p>
                      {report.mode === "service" ||
                      (Array.isArray(report.filters) &&
                        report.filters.some(
                          (f) => typeof f === "object" && f?.type,
                        ))
                        ? this.renderStandardFilters(report)
                        : this.renderLegacyFilters(report, schema)}
                      {this.renderActionBar()}
                    </CardView>
                  </div>

                  {!generated && !loading && objects.length === 0 && (
                    <CardView style={{ borderRadius: 0, marginTop: 16 }}>
                      <div className="text-center text-muted py-4">
                        Set filters and click <strong>Generate</strong> to load
                        this report. Data is not fetched until you generate.
                      </div>
                    </CardView>
                  )}

                  {(generated || objects.length > 0) && (
                    <>
                      <CardView style={{ borderRadius: 0, marginTop: 16 }}>
                        <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                          <div>
                            <h5 className="text-primary mb-1">{report.name}</h5>
                            {summary ? (
                              <p
                                className="text-muted mb-0"
                                style={{ fontSize: ".85rem" }}
                              >
                                {summary}
                              </p>
                            ) : (
                              <p
                                className="text-muted mb-0"
                                style={{ fontSize: ".85rem" }}
                              >
                                Records: {count}
                              </p>
                            )}
                          </div>
                          <span
                            className="badge bg-primary text-secondary border"
                            style={{ fontSize: ".8rem", fontWeight: 500 }}
                          >
                            {count} row{count === 1 ? "" : "s"}
                          </span>
                        </div>
                      </CardView>
                      <Table
                        certificate
                        progress={loading || isLoading}
                        refetch={this.refetchData.bind(this)}
                        viewService={(obj) =>
                          this.presenter.handleActionBtn(
                            obj,
                            "isMovementHistory",
                          )
                        }
                        viewLeaves={(obj) =>
                          this.presenter.handleActionBtn(obj, "isLeaveBalance")
                        }
                        viewSalary={(obj) =>
                          this.presenter.handleActionBtn(
                            obj,
                            "isNoticeSalaryAdjustment",
                          )
                        }
                        viewStepIncrement={(obj) =>
                          this.presenter.handleActionBtn(obj, "isNoticeOfStep")
                        }
                        hrAction={this.viewOrgStructure.bind(this)}
                        stepIncrement={this.viewAssignedBio.bind(this)}
                        fields={schema.fields}
                        objectData={(obj) =>
                          this.presenter.otherExpencesAward?.(obj, schema)
                        }
                        objects={objects}
                        collection={schema}
                        count={count}
                        limit={limit}
                        current={current}
                        loadPagination={this.onClickPagination.bind(this)}
                        actionsList={report.actionList}
                        excludeFields={excludeFields}
                        onClickItem={this.onClickItem.bind(this)}
                      />
                    </>
                  )}
                </>
              )}
            </div>
          </InfiniteScroll>
        </div>

        {this.state.isMovementHistory && (
          <ServiceRecordsForm
            closeModal={() => this.setState({ isMovementHistory: false })}
            email={object.email}
            data={object}
            reportLists="Contract of Service"
          />
        )}
        {this.state.isLeaveBalance && (
          <LeavesRecordsForm
            closeModal={() => this.setState({ isLeaveBalance: false })}
            email={object.email}
            data={object}
            reportLists="Contract of Service"
          />
        )}
        {this.state.isNoticeSalaryAdjustment && (
          <SalaryAdjustmentPage
            closeModal={() =>
              this.setState({ isNoticeSalaryAdjustment: false })
            }
            email={object.email}
            data={object}
            reportLists="Contract of Service"
          />
        )}
        {this.state.isNoticeOfStep && (
          <StepIncrementPage
            closeModal={() => this.setState({ isNoticeOfStep: false })}
            email={object.email}
            data={object}
            reportLists="Contract of Service"
          />
        )}
        {this.state.viewOrg && (
          <ViewOrganizationalStructure
            employee={this.state.employee}
            handleCancel={() => this.setState({ viewOrg: false })}
          />
        )}
        {this.state.viewBio && (
          <ViewAssignedDevices
            closeModal={() => this.setState({ viewBio: false })}
            assignedDevice={this.state.assignedDevice}
          />
        )}
      </>
    );
  }
}

export default withRouter(ReportPage);
